import { Controller } from '@hotwired/stimulus'

// Floating kebab/actions menu that escapes overflow-clipped ancestors.
// The menu is rendered inline next to the trigger but positioned with
// `position: fixed`, so it is not clipped by the table card.
export default class extends Controller {
  static targets = ['trigger', 'menu']

  connect() {
    this.boundOnDocumentClick = this.onDocumentClick.bind(this)
    this.boundOnKeydown = this.onKeydown.bind(this)
    this.boundReposition = this.reposition.bind(this)
    this.close()
  }

  disconnect() {
    this.removeOpenListeners()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    // Close any other menus that may be open.
    document.querySelectorAll('[data-kebab-menu-open]').forEach((el) => {
      if (el !== this.element) el.removeAttribute('data-kebab-menu-open')
    })
    document.dispatchEvent(new CustomEvent('kebab-menu:open', { detail: { element: this.element } }))

    this.menuTarget.classList.remove('hidden')
    this.element.setAttribute('data-kebab-menu-open', '')
    this.triggerTarget.setAttribute('aria-expanded', 'true')
    this.reposition()

    document.addEventListener('click', this.boundOnDocumentClick, true)
    document.addEventListener('keydown', this.boundOnKeydown)
    window.addEventListener('scroll', this.boundReposition, true)
    window.addEventListener('resize', this.boundReposition)
  }

  close() {
    this.menuTarget.classList.add('hidden')
    this.element.removeAttribute('data-kebab-menu-open')
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute('aria-expanded', 'false')
    this.removeOpenListeners()
  }

  removeOpenListeners() {
    document.removeEventListener('click', this.boundOnDocumentClick, true)
    document.removeEventListener('keydown', this.boundOnKeydown)
    window.removeEventListener('scroll', this.boundReposition, true)
    window.removeEventListener('resize', this.boundReposition)
  }

  get isOpen() {
    return this.element.hasAttribute('data-kebab-menu-open')
  }

  onDocumentClick(event) {
    if (this.element.contains(event.target)) return
    this.close()
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close()
      this.triggerTarget.focus()
    }
  }

  reposition() {
    const triggerRect = this.triggerTarget.getBoundingClientRect()
    const menu = this.menuTarget
    // Measure after making it visible (still allowed since hidden was removed).
    const menuRect = menu.getBoundingClientRect()
    const margin = 4

    // Open to the left of the trigger by default (matches old dropdown-left).
    let left = triggerRect.left - menuRect.width - margin
    if (left < margin) left = triggerRect.right + margin // fall back to right if no room

    // Anchor vertically to the trigger; flip upward if it would overflow.
    let top = triggerRect.top
    if (top + menuRect.height + margin > window.innerHeight) {
      top = Math.max(margin, triggerRect.bottom - menuRect.height)
    }

    menu.style.position = 'fixed'
    menu.style.top = `${top}px`
    menu.style.left = `${left}px`
  }
}
